import { ValidateBrokerLoginResponse } from 'proto/ea-hosting_pb';

export type LoginCheck = ValidateBrokerLoginResponse.AsObject;

// ValidateBrokerLogin result values (ea-manager proto).
export const LOGIN_RESULT = {
  checking: 'checking',
  ok: 'ok',
  serverUnresolvable: 'server_unresolvable',
  loginFailed: 'login_failed',
  loginUnconfirmed: 'login_unconfirmed',
  tradingDisabled: 'trading_disabled',
  noConnection: 'no_connection',
  unavailable: 'unavailable'
} as const;

const BLOCKING_RESULTS: string[] = [
  LOGIN_RESULT.serverUnresolvable,
  LOGIN_RESULT.loginFailed,
  LOGIN_RESULT.loginUnconfirmed,
  LOGIN_RESULT.tradingDisabled
];

// ok continues; warn continues with a free-text Symbol; block stops until the profile is fixed.
export type LoginGate = 'checking' | 'ok' | 'warn' | 'block';

// An unknown future result reads as a warning, so it can't block a launch.
export const loginGate = (result: string): LoginGate => {
  if (result === LOGIN_RESULT.checking) return 'checking';
  if (result === LOGIN_RESULT.ok) return 'ok';
  return BLOCKING_RESULTS.includes(result) ? 'block' : 'warn';
};

// Launch (and the rail step) need a settled, non-blocking result; a running check holds them.
export const canContinue = (gate: LoginGate | null): boolean => gate === 'ok' || gate === 'warn';

// A result with no server detail, for "checking" before the first response and for failed calls.
export const loginPlaceholder = (result: string): LoginCheck => ({
  result,
  message: '',
  symbolsComplete: false,
  symbolCount: 0,
  symbolsVersion: ''
});

export const LOGIN_POLL_INTERVAL_MS = 1500;
// A check takes ~2–7 s; past this the profile reads as unverified (a warning, not a block).
export const LOGIN_POLL_TIMEOUT_MS = 60000;

interface PollOptions {
  recheck?: boolean;
  // True once the result no longer matters (profile switched, form closed, page left).
  isStale?: () => boolean;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Polls until the result leaves "checking"; only the first call may recheck, later ones just read it. null = stale.
export const pollBrokerLogin = async (
  validate: (recheck: boolean) => Promise<LoginCheck>,
  { recheck = false, isStale = () => false, now = Date.now, sleep = wait }: PollOptions = {}
): Promise<LoginCheck | null> => {
  const deadline = now() + LOGIN_POLL_TIMEOUT_MS;
  let res = await validate(recheck);
  while (res.result === LOGIN_RESULT.checking) {
    if (isStale()) return null;
    if (now() >= deadline) return { ...res, result: LOGIN_RESULT.unavailable };
    await sleep(LOGIN_POLL_INTERVAL_MS);
    if (isStale()) return null;
    res = await validate(false);
  }
  return isStale() ? null : res;
};

// Ms until "Check again" may run; 0 when next_check_at is unset or has passed.
export const recheckWaitMs = ({ nextCheckAt }: LoginCheck, nowMs: number): number =>
  nextCheckAt ? Math.max(0, nextCheckAt.seconds * 1000 - nowMs) : 0;

// CreateBrokerProfile rejects an unknown server outright; the engine's message is the only marker.
const SERVER_NOT_FOUND_MARKER = 'could not be resolved';
export const isServerNotFound = (err: unknown): boolean =>
  String((err as { message?: unknown } | null)?.message ?? '').includes(SERVER_NOT_FOUND_MARKER);
